<template>
  <!-- «Какой заказ?» в форме (f10-f14) -->
  <div class="order-picker">
    <div class="order-picker__label">
      Какой заказ?
      <span v-if="!picked" class="order-picker__required">*обязательно выберите заказ</span>
    </div>

    <!-- Выбранный заказ - кремовая плашка со «Сменить» (f12) -->
    <div v-if="value.order" class="order-picker__picked">
      <v-icon size="22" class="order-picker__picked-icon">
        mdi-receipt-text-outline
      </v-icon>
      <div class="order-picker__picked-text">
        <div class="order-picker__picked-title">
          Заказ №{{ value.order.number }} · {{ money(value.order.total) }} · {{ method(value.order) }}
        </div>
        <div class="order-picker__picked-sub">
          {{ value.order.status.label }}
        </div>
      </div>
      <v-btn
        depressed
        height="36"
        class="order-picker__change text-normalize"
        @click="change"
      >
        Сменить
      </v-btn>
    </div>

    <!-- FAKT CRM не отвечает: номер принимаем текстом, без проверки (Q33) -->
    <template v-else-if="crmDown">
      <div class="order-picker__search">
        <input
          class="order-picker__input"
          :value="value.orderNumber || ''"
          inputmode="numeric"
          placeholder="Номер заказа"
          @input="typeNumber($event.target.value)"
        >
      </div>
      <div class="order-picker__plate">
        <v-icon size="20" class="order-picker__plate-icon">
          mdi-information-outline
        </v-icon>
        FAKT CRM сейчас не отвечает - отправим номер заказа как есть
      </div>
    </template>

    <template v-else>
      <div class="order-picker__search-wrap">
        <div class="order-picker__search" :class="{ 'order-picker__search--error': notFound }">
          <input
            v-model="query"
            class="order-picker__input"
            inputmode="numeric"
            placeholder="Найти заказ по номеру"
            @input="scheduleSearch"
          >
          <v-icon size="20" class="order-picker__search-icon">
            mdi-magnify
          </v-icon>
        </div>

        <!-- Совпадения по всем заказам филиала, включая закрытые (f11) -->
        <div v-if="results.length" class="order-picker__dropdown">
          <div class="order-picker__found">
            {{ found }}
          </div>
          <button
            v-for="(order, index) in results"
            :key="order.id"
            type="button"
            class="order-picker__result"
            :class="{ 'order-picker__result--first': index === 0 }"
            @click="pick(order)"
          >
            <span class="order-picker__number">№<template v-for="(part, i) in highlight(order.number)"><b v-if="part.hit" :key="i">{{ part.text }}</b><template v-else>{{ part.text }}</template></template></span>
            <span class="order-picker__total">{{ money(order.total) }}</span>
            <span class="order-picker__method">{{ method(order) }}</span>
            <span class="order-picker__status">
              <span class="order-picker__dot" :style="dotStyle(order)" />
              {{ order.status.label }}
            </span>
          </button>
        </div>
      </div>

      <div v-if="notFound" class="order-picker__error">
        Заказ №{{ searched }} не найден. Проверьте номер и повторите поиск
      </div>

      <div v-if="loadingOpen" class="order-picker__loading">
        <v-progress-circular
          indeterminate
          size="20"
          width="2"
          color="primary"
        />
      </div>
      <div v-else-if="!openOrders.length" class="order-picker__plate">
        <v-icon size="20" class="order-picker__plate-icon">
          mdi-information-outline
        </v-icon>
        Открытых заказов в филиале сейчас нет - найдите номер заказа в поиске
      </div>
      <template v-else>
        <div class="order-picker__caption">
          <b>Заказы в работе</b> · для выбора завершённого заказа воспользуйтесь поиском по номеру заказа
        </div>
        <div class="order-picker__list" role="radiogroup">
          <button
            v-for="order in openOrders"
            :key="order.id"
            type="button"
            role="radio"
            :aria-checked="false"
            class="order-picker__row"
            @click="pick(order)"
          >
            <span class="order-picker__radio" />
            <span class="order-picker__number">№{{ order.number }}</span>
            <span class="order-picker__total">{{ money(order.total) }}</span>
            <span class="order-picker__method">{{ method(order) }}</span>
            <span class="order-picker__status">
              <span class="order-picker__dot" :style="dotStyle(order)" />
              {{ order.status.label }}
            </span>
          </button>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
  import supportApi, { supportErrorCode } from '@/api/support'
  import { money } from '../format'
  import { foundText, highlight, searchQuery } from '../form'

  // Ищем, когда человек перестал печатать, а не на каждую цифру.
  const SEARCH_DELAY_MS = 300

  export default {
    name: 'OrderPicker',
    props: {
      // { order: CrmOrder | null, orderNumber: string | null }
      value: { type: Object, required: true },
    },
    data () {
      return {
        openOrders: [],
        loadingOpen: true,
        crmDown: false,
        query: '',
        searched: null,
        results: [],
        total: 0,
        notFound: false,
        searchTimer: null,
      }
    },
    computed: {
      picked () {
        return Boolean(this.value.order || (this.crmDown && this.value.orderNumber))
      },
      found () {
        return foundText(this.total)
      },
    },
    created () {
      this.loadOpen()
    },
    beforeDestroy () {
      clearTimeout(this.searchTimer)
    },
    methods: {
      money,
      method (order) {
        return order.method === 'pickup' ? 'Самовывоз' : 'Доставка'
      },
      highlight (number) {
        return highlight(number, this.searched)
      },
      // Цвет точки - как у статуса в листинге CRM; нет цвета - серая.
      dotStyle (order) {
        return order.status.color ? { background: order.status.color } : {}
      },
      async loadOpen () {
        this.loadingOpen = true
        try {
          this.openOrders = (await supportApi.orders()).items
        } catch (e) {
          if (supportErrorCode(e) === 'crm_unavailable') this.crmDown = true
        } finally {
          this.loadingOpen = false
        }
      },
      scheduleSearch () {
        clearTimeout(this.searchTimer)
        this.notFound = false
        const q = searchQuery(this.query)
        if (!q) {
          this.results = []
          return
        }
        this.searchTimer = setTimeout(() => this.search(q), SEARCH_DELAY_MS)
      },
      async search (q) {
        try {
          const data = await supportApi.orders(q)
          // Пока ждали ответ, номер поменяли: этот ответ уже не нужен.
          if (searchQuery(this.query) !== q) return
          this.searched = q
          this.results = data.items
          this.total = data.total
          this.notFound = data.items.length === 0
        } catch (e) {
          if (supportErrorCode(e) === 'crm_unavailable') this.crmDown = true
        }
      },
      pick (order) {
        this.results = []
        this.query = ''
        this.notFound = false
        this.$emit('input', { order, orderNumber: null })
      },
      change () {
        this.$emit('input', { order: null, orderNumber: null })
      },
      typeNumber (text) {
        this.$emit('input', { order: null, orderNumber: text.replace(/\D/g, '') || null })
      },
    },
  }
</script>

<style scoped lang="less">
@import '../tokens.less';

.order-picker__label {
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 500;
  color: @fakt-text;
}

.order-picker__required {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 400;
  color: @fakt-pink;
}

.order-picker__search-wrap {
  position: relative;
}

.order-picker__search {
  display: flex;
  align-items: center;
  height: 44px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: @fakt-surface;

  &:focus-within {
    border-color: @fakt-orange;
  }

  &--error,
  &--error:focus-within {
    border-color: @fakt-error;
  }
}

.order-picker__input {
  flex: 1;
  font-size: 14px;
  outline: none;
  color: @fakt-text;
}

.order-picker__search-icon {
  color: @fakt-text-2 !important;
}

.order-picker__dropdown {
  position: absolute;
  top: 48px;
  right: 0;
  left: 0;
  z-index: 5;
  padding: 8px 6px;
  border-radius: 8px;
  background: @fakt-white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, .16);
}

.order-picker__found {
  padding: 4px 12px 8px;
  font-size: 12px;
  color: @fakt-text-2;
}

.order-picker__result,
.order-picker__row {
  display: flex;
  gap: 16px;
  align-items: center;
  width: 100%;
  height: 44px;
  padding: 0 12px;
  font-size: 14px;
  text-align: left;
  color: @fakt-text;

  &:hover {
    background: @fakt-surface;
  }
}

.order-picker__result {
  border-radius: 6px;

  &--first {
    background: @fakt-surface;
  }
}

.order-picker__number {
  width: 64px;
  font-weight: 500;
}

// В результатах поиска жирное - только совпавшие цифры (f11)
.order-picker__result .order-picker__number {
  font-weight: 400;

  b {
    font-weight: 600;
  }
}

.order-picker__total {
  width: 78px;
}

.order-picker__method {
  flex: 1;
  color: @fakt-text-2;
}

.order-picker__status {
  display: inline-flex;
  gap: 6px;
  align-items: center;
}

.order-picker__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: @fakt-text-3;
}

.order-picker__error {
  margin-top: 6px;
  font-size: 12px;
  color: @fakt-error;
}

.order-picker__caption {
  margin: 12px 0 8px;
  font-size: 12px;
  color: @fakt-text-2;

  b {
    font-size: 13px;
    font-weight: 500;
    color: @fakt-text;
  }
}

.order-picker__list {
  overflow: hidden;
  border: 1px solid @fakt-divider;
  border-radius: 10px;

  .order-picker__row + .order-picker__row {
    border-top: 1px solid @fakt-divider;
  }
}

.order-picker__radio {
  width: 18px;
  height: 18px;
  border: 2px solid @fakt-card-border;
  border-radius: 50%;
}

.order-picker__loading {
  padding: 16px;
  text-align: center;
}

.order-picker__plate {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 13px;
  color: @fakt-text;
  background: @fakt-cream-plate;
}

.order-picker__plate-icon {
  color: @fakt-orange !important;
}

.order-picker__picked {
  display: flex;
  gap: 12px;
  align-items: center;
  min-height: 60px;
  padding: 0 12px 0 16px;
  border-radius: 10px;
  background: @fakt-cream-plate;
}

.order-picker__picked-icon {
  color: @fakt-orange !important;
}

.order-picker__picked-text {
  flex: 1;
}

.order-picker__picked-title {
  font-size: 14px;
  font-weight: 500;
  color: @fakt-text;
}

.order-picker__picked-sub {
  font-size: 12px;
  color: @fakt-text-2;
}

.order-picker__change {
  border-radius: 8px;
  font-size: 13px;
  background-color: @fakt-white !important;
}
</style>
